import { format } from 'date-fns';
import { FilterValue, TableColumn, TableFilterOptions, TableRow, TagColumn } from '../../types/responsiveTable';
import { getDropdownOptions, getTagOptions } from '../TableFilter/TableFilter';
import type { NumericFilterValue } from '../NumericFilter/NumericFilter';

// The day format of the date filters (the Calendar input shows dd/mm/yy; the chip spells the year in full)
const DAY_FORMAT = 'dd/MM/yyyy';
const MODE_SIGN: Record<string, string> = { 'greater-than': '>', 'less-than': '<', equal: '=' };
/** More values than this show only the first `SHOWN_OF_MANY` and the number of the rest. */
const MAX_SHOWN = 3;
const SHOWN_OF_MANY = 2;

const joinTexts = (texts: string[]): string =>
  texts.length > MAX_SHOWN ? `${texts.slice(0, SHOWN_OF_MANY).join(', ')} +${texts.length - SHOWN_OF_MANY}` : texts.join(', ');

const numberText = <T extends TableRow>(column: TableColumn<T>, { mode, value }: NumericFilterValue): string => {
  const amount = value as number;
  const formatted =
    column.type === 'number' && column.isCurrency
      ? new Intl.NumberFormat(column.currencyLocale ?? 'es-ES', {
          style: 'currency',
          currency: column.currencyCode ?? 'EUR',
          minimumFractionDigits: 2,
        }).format(amount)
      : new Intl.NumberFormat('es-ES', { maximumFractionDigits: 20 }).format(amount);
  return `${MODE_SIGN[mode]} ${formatted}`;
};

const booleanText = <T extends TableRow>(column: TableColumn<T>, value: FilterValue): string => {
  const { trueText, falseText } = column as { trueText?: string; falseText?: string };
  if (value === true) return trueText || 'Sí';
  if (value === false) return falseText || 'No';
  return 'No aplica';
};

/** The label an option list gives to `value`, or the value itself as text when the list does not know it. */
const optionLabel = (options: Array<{ label: string; value: unknown }>, value: FilterValue): string =>
  options.find((option) => option.value === value)?.label ?? String(value ?? '-');

const calendarText = (values: FilterValue[]): string => {
  const days = values.map((value) => format(value as Date, DAY_FORMAT));
  return days.length > 1 ? `${days[0]} – ${days[1]}` : `desde ${days[0]}`;
};

/**
 * The values of one applied filter as the filter input shows them: option names (not ids), the sign of a number, `dd/MM/yyyy`
 * days, a progress range, a text between quotes. `filterOptions` and `data` are the ones the filter input reads.
 */
export const appliedFilterText = <T extends TableRow>(
  column: TableColumn<T>,
  values: FilterValue[],
  filterOptions: TableFilterOptions | undefined,
  data: T[],
): string => {
  switch (column.type) {
    case 'string':
      return joinTexts(values.map((value) => `"${value}"`));
    case 'dropdown': {
      const options = getDropdownOptions(column, filterOptions, data);
      return joinTexts(values.map((value) => optionLabel(options, value)));
    }
    case 'tag': {
      const options = getTagOptions(column as TagColumn<T>, filterOptions, data);
      return joinTexts(values.map((value) => optionLabel(options, value)));
    }
    case 'boolean':
      return joinTexts(values.map((value) => booleanText(column, value)));
    case 'number':
      return joinTexts(values.map((value) => numberText(column, value as NumericFilterValue)));
    case 'calendar':
      return calendarText(values);
    default:
      // progress (an avatar column is never filterable, so it never gets here)
      return `${values[0]} % – ${values[1]} %`;
  }
};
